import DiffMatchPatch from 'diff-match-patch';
import { useMemo } from 'react';

import { DiffChangeType } from '@/types/diff';
import { cn } from '@/utils/tailwind';

export const useGeneratePromptToShow = ({
  promptToShow,
  previousPrompt,
  highlightedRangeRef,
  showManualHighlight,
  isMutating,
  handleManualPromptUpdate,
  resetState,
  isStale,
  textRef,
  showDiff,
}: {
  promptToShow: string;
  previousPrompt?: string;
  highlightedRangeRef: React.RefObject<[number, number]>;
  showManualHighlight: boolean;
  isMutating: boolean;
  handleManualPromptUpdate: (newPrompt: string) => void;
  resetState: () => void;
  isStale: React.MutableRefObject<boolean>;
  textRef: React.RefObject<HTMLSpanElement>;
  showDiff: boolean;
}) => {
  const promptDiffs: DiffChangeType[] = useMemo(() => {
    if (!showDiff || !previousPrompt) return [[0, promptToShow]];

    const dmp = new DiffMatchPatch();

    const diffs = dmp.diff_main(previousPrompt, promptToShow);
    dmp.diff_cleanupSemantic(diffs);

    return diffs;
  }, [previousPrompt, promptToShow, showDiff]);

  const renderedPrompt = useMemo(() => {
    if (showDiff) {
      return promptDiffs.map(([changeType, text]) => {
        if (changeType === 0)
          return (
            <span className="leading-[1.6em] focus:outline-none">{text}</span>
          );
        return (
          <span
            className={cn(
              'leading-[1.6em] focus:outline-none',
              changeType === 1 ? 'bg-success-secondary' : 'bg-error-secondary'
            )}
          >
            {text}
          </span>
        );
      });
    }

    if (!showManualHighlight) {
      return (
        <span
          ref={textRef}
          contentEditable={!isMutating}
          suppressContentEditableWarning
          tabIndex={0}
          onInput={() => {
            const newText = textRef.current?.innerText;
            if (newText) {
              isStale.current = true;
              resetState();
            }
          }}
          onBlur={() => {
            if (isStale.current && textRef.current?.innerText) {
              handleManualPromptUpdate(textRef.current.innerText);
              isStale.current = false;
            }
          }}
          className="leading-[1.6em] focus:outline-none"
        >
          {promptToShow}
        </span>
      );
    }
    if (highlightedRangeRef.current) {
      const [start, end] = highlightedRangeRef.current;
      // showing manual highlight so beforeHighlight, highlight, afterHighlight separately
      return [
        promptToShow.slice(0, start),
        promptToShow.slice(start, end),
        promptToShow.slice(end),
      ].map((part, index) => (
        <span
          tabIndex={0}
          key={index}
          className={cn(
            'inline leading-[1.6em]',
            index === 1 &&
              'mb-[-0.2em] mt-[-0.1em] bg-brand-green-200 pb-[0.2em] pt-[0.1em] dark:bg-brand-primary'
          )}
        >
          {part}
        </span>
      ));
    }
    return null;
  }, [
    highlightedRangeRef.current,
    showManualHighlight,
    showDiff,
    promptDiffs,
    promptToShow,
    isMutating,
  ]);

  return {
    renderedPrompt,
  };
};
